import { UNSTABLE_ToastQueue as RACToastQueue } from 'react-aria-components';
import { flushSync } from 'react-dom';
import type { InternalToastContent, ToastOptions } from './types.js';

const MIN_TOAST_TIMEOUT = 5000;

export const internalToastQueue = new RACToastQueue<InternalToastContent>({
  wrapUpdate(fn) {
    if ('startViewTransition' in document) {
      document.startViewTransition(() => {
        flushSync(fn);
      });
    } else {
      fn();
    }
  },
});

function addToast(content: InternalToastContent, options?: ToastOptions) {
  let timeout = options?.timeout ?? MIN_TOAST_TIMEOUT;

  if (timeout > 0 && timeout < MIN_TOAST_TIMEOUT) {
    console.warn(
      `[ToastQueue] The specified timeout (${timeout} ms) is less than the minimum allowed (${MIN_TOAST_TIMEOUT} ms). It will be adjusted to the minimum.`,
    );

    timeout = MIN_TOAST_TIMEOUT;
  }

  return internalToastQueue.add(content, { ...options, timeout });
}

/**
 * Manages the queue of toasts.
 *
 * See [toast usage guidelines](https://ui.cimpress.io/components/toast/).
 */
export interface ToastQueue {
  /**
   * Add an info toast to the queue.
   * @returns The ID of the toast
   */
  info(message: string, options?: ToastOptions): string;
  /**
   * Add a success toast to the queue.
   * @returns The ID of the toast
   */
  success(message: string, options?: ToastOptions): string;
  /**
   * Add a warning toast to the queue.
   * @returns The ID of the toast
   */
  warning(message: string, options?: ToastOptions): string;
  /**
   * Add a critical toast to the queue.
   * @returns The ID of the toast
   */
  critical(message: string, options?: ToastOptions): string;
  /**
   * Close a toast.
   * @param id The ID of the toast to close
   */
  close(id: string): void;
  /**
   * Clear the queue.
   */
  clear(): void;
}

export const toastQueue: ToastQueue = {
  info(message: string, options?: ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'info',
      },
      options,
    );
  },
  success(message: string, options?: ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'success',
      },
      options,
    );
  },
  warning(message: string, options?: ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'warning',
      },
      options,
    );
  },
  critical(message: string, options?: ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'critical',
      },
      options,
    );
  },
  close(id: string) {
    internalToastQueue.close(id);
  },
  clear() {
    internalToastQueue.clear();
  },
};
